import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import { ROL_LABEL } from '@leadsphere/types';

import { useAuth } from '../../hooks/useAuth.js';
import { NAV_GROUPS } from './nav-config.js';

const COLLAPSE_KEY = 'ls-sidebar-collapsed';

function navItemClass({ isActive }: { isActive: boolean }): string {
  return `nav-item${isActive ? ' active' : ''}`;
}

function MenuToggleIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  );
}

// Estado puramente de UI (preferencia local, no persiste en backend) — Prompt:
// Restyle visual. No afecta rutas/permisos, solo cuánto sidebar se muestra.
export function Sidebar() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const iniciales = user?.correo ? user.correo.slice(0, 2).toUpperCase() : '??';

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0');
    } catch {
      // localStorage no disponible (ej. modo privado) — el toggle sigue
      // funcionando en memoria para esta sesión, solo no se recuerda.
    }
  }, [collapsed]);

  return (
    <aside className={`sidebar${collapsed ? ' collapsed' : ''}`}>
      <div className="sidebar-logo">
        <div className="sidebar-logo-wrap">
          <div className="sidebar-logo-mark">NX</div>
          <div className="sidebar-logo-text">
            Nexus
            <span>Leadsphere Connect</span>
          </div>
        </div>
        <button
          className="sidebar-toggle"
          onClick={() => setCollapsed(c => !c)}
          aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
          title={collapsed ? 'Expandir menú' : 'Colapsar menú'}
        >
          <MenuToggleIcon />
        </button>
      </div>

      <nav className="sidebar-nav">
        {NAV_GROUPS.map(group => {
          const items = group.items.filter(item => {
            if (!item.roles && !item.correos) return true;
            if (item.roles && user && item.roles.includes(user.rol)) return true;
            if (item.correos && user && item.correos.includes(user.correo)) return true;
            return false;
          });
          if (items.length === 0) return null;

          return (
            <div key={group.label}>
              <div className="nav-label">{group.label}</div>
              {items.map(({ to, label, end, Icon, isActive, disabled }) =>
                disabled ? (
                  <span key={to} className="nav-item nav-item--disabled" title={label}>
                    <Icon />
                    <span>{label}</span>
                  </span>
                ) : (
                  <NavLink
                    key={to}
                    to={to}
                    end={end}
                    className={isActive ? `nav-item${isActive(pathname) ? ' active' : ''}` : navItemClass}
                    title={label}
                  >
                    <Icon />
                    <span>{label}</span>
                  </NavLink>
                )
              )}
            </div>
          );
        })}
      </nav>

      <div className="sidebar-footer">
        <div className="sidebar-user">
          <div className="sidebar-avatar">{iniciales}</div>
          <div className="sidebar-user-info" style={{ minWidth: 0 }}>
            <div className="sidebar-user-name">{user?.correo ?? 'Usuario'}</div>
            <div className="sidebar-user-role">{user?.rol ? (ROL_LABEL[user.rol] ?? user.rol) : ''}</div>
          </div>
        </div>
        <button className="sidebar-logout" onClick={logout} title="Cerrar sesión">
          <LogoutIcon />
          <span>Cerrar sesión</span>
        </button>
      </div>
    </aside>
  );
}
